import { FC, useEffect, useRef } from 'react'

import { IconClear, IconSearch } from '@avantstay/backoffice-vectors'

import { useGoogleApi } from '../../hooks'
import { Margin } from '../Margin'
import { TextField } from '../TextField'
import { AddressInputProps, AutoCompleteAddress } from './PlaceAutoComplete.types'

interface AddressFieldProps extends AddressInputProps {
  className?: string
  onAutocompleteSelection?: (value: AutoCompleteAddress) => void
}

// @ts-ignore
type AutoCompleteMaps = google.maps.places.Autocomplete

export const PlaceAutoComplete: FC<AddressFieldProps> = ({
  searchTerm,
  onAutocompleteSelection,
  onChange,
  hasSearchIcon = true,
  iconSearchSize,
  ...restProps
}) => {
  const autocompleteRef = useRef<AutoCompleteMaps>()
  const inputRef = useRef<HTMLInputElement | null>(null)
  const onAutocompleteSelectionRef = useRef(onAutocompleteSelection)
  const onChangeRef = useRef(onChange)

  onAutocompleteSelectionRef.current = onAutocompleteSelection
  onChangeRef.current = onChange

  const handleClearInput = () => {
    const inputElement = inputRef.current
    if (!inputElement) return

    inputElement.value = ''
    onChange(undefined)
  }

  const { loaded: isScriptLoaded } = useGoogleApi()

  const placeChangedListenerRef = useRef<any>(null)

  useEffect(() => {
    if (isScriptLoaded) {
      const inputElement = inputRef.current
      if (!inputElement) return

      const { maps } = (window as any).google

      const autocomplete =
        autocompleteRef.current ||
        new maps.places.Autocomplete(inputElement, {
          types: ['geocode'],
          componentRestrictions: { country: 'us' },
        })

      autocompleteRef.current = autocomplete

      // Store the listener in the ref
      placeChangedListenerRef.current = autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace()

        onAutocompleteSelectionRef.current?.({
          plainAddressText: place.formatted_address,
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        })
      })
    }
  }, [isScriptLoaded])

  useEffect(() => {
    return () => {
      // Remove listener on component unmount
      if (placeChangedListenerRef.current) {
        const { maps } = (window as any).google
        maps.event.removeListener(placeChangedListenerRef.current)
      }
    }
  }, [])

  const clearButton = () =>
    searchTerm && searchTerm?.trim().length > 0 ? (
      <IconClear aria-label="Clear search filter" role="button" onClick={handleClearInput} />
    ) : null

  const searchIcon = () =>
    hasSearchIcon ? (
      <Margin right={4}>
        <IconSearch {...iconSearchSize} />
      </Margin>
    ) : null

  return (
    <TextField.Small
      {...restProps}
      value={searchTerm}
      inputLeftIcon={searchIcon()}
      inputRightIcon={clearButton()}
      ref={inputRef}
      onChange={event => onChangeRef.current?.(event)}
    />
  )
}
